import * as React from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import Dialog from "src/components/Dialog";
// import Icon from "../Icon";
import Link from "next/link";
import Button from "@src/components/Button";
import { GetAlbumDetailsQuery } from "@src/@types/api.d";

type Props = {
  eventId: number;
  albumDetailsData: GetAlbumDetailsQuery;
  eventSlug: string;
};

export function insertAt(
  array: unknown[],
  index: number,
  ...elementsArray: unknown[]
) {
  array.splice(index, 0, ...elementsArray);
}

const Masonry: React.FC<Props> = ({ eventId, eventSlug, albumDetailsData }) => {
  const mediaData = albumDetailsData?.getMedia || [];

  // filter to only image media
  const imageMedia = React.useMemo(
    () =>
      (mediaData || []).filter(
        (m) =>
          m &&
          typeof m?.type === "string" &&
          m.type.toLowerCase().includes("image")
      ),
    [mediaData]
  );

  return !!imageMedia && imageMedia?.length > 0 ? (
    <div className="bg-[#FCF8EB] pt-[5rem] pb-[3rem] px-4">
      <h2 className="text-center text-4xl lg:text-6xl font-caudex italic font-semibold text-[#75264E] mt-[-1rem] mb-[2rem]">
        Picture Perfect Love
      </h2>

      {/* Grid layout for screens smaller than lg */}
      <div className="block lg:hidden">
        <div
          className={`grid max-w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 md:gap-6 mb-12`}
        >
          {imageMedia?.slice(0, 4)?.map((image, index) => {
            if (image && image.meta?.s3_url) {
              return (
                <div
                  key={index}
                  className={`relative aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02]`}
                >
                  <Image
                    placeholder="blur"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={image.meta.s3_url}
                    layout="fill"
                    alt="gallery"
                    objectFit="cover"
                    className="rounded-2xl object-top"
                  />
                </div>
              );
            }
          })}
        </div>
      </div>

      {/* Horizontal scrolling layout for lg screens and above */}
      <div className="hidden lg:block mb-12">
        <div className="relative overflow-hidden">
          <div
            className="flex gap-6 animate-scroll"
            style={{
              width: `${
                imageMedia.length * 320 + (imageMedia.length - 1) * 24
              }px`, // 320px width + 24px gap
            }}
          >
            {/* First set of images */}
            {imageMedia?.map((image, index) => {
              if (image && image.meta?.s3_url) {
                return (
                  <div
                    key={`first-${index}`}
                    className="relative w-80 aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02] flex-shrink-0"
                  >
                    <Image
                      placeholder="blur"
                      blurDataURL={`data:image/svg+xml;base64,${toBase64(
                        shimmer(700, 475)
                      )}`}
                      src={image.meta.s3_url}
                      layout="fill"
                      alt="gallery"
                      objectFit="cover"
                      className="rounded-2xl object-top"
                    />
                  </div>
                );
              }
            })}
            {/* Duplicate set for seamless loop */}
            {imageMedia?.map((image, index) => {
              if (image && image.meta?.s3_url) {
                return (
                  <div
                    key={`second-${index}`}
                    className="relative w-80 aspect-[0.865] cursor-pointer rounded-2xl transition duration-500 hover:scale-[1.02] flex-shrink-0"
                  >
                    <Image
                      placeholder="blur"
                      blurDataURL={`data:image/svg+xml;base64,${toBase64(
                        shimmer(700, 475)
                      )}`}
                      src={image.meta.s3_url}
                      layout="fill"
                      alt="gallery"
                      objectFit="cover"
                      className="rounded-2xl object-top"
                    />
                  </div>
                );
              }
            })}
          </div>
        </div>
      </div>

      <div
        className={`flex flex-col md:flex-row gap-8 justify-center items-center`}
      >
        <Link
          href={`https://${eventSlug}.${process.env.NEXT_PUBLIC_INAWO_ALBUM_SITE_END}`}
          passHref
        >
          <a target="_blank">
            <Button
              design="transparent"
              type="button"
              label="Save_Date"
              className="!border-[#75264E] !p-0"
            >
              <span className="flex items-center justify-center h-[4rem] w-[12rem] !font-corGaramond text-lg !text-[#75264E] border border-[#75264E] rounded capitalize font-bold hover:scale-105 transition-transform duration-300">
                See full photobook
              </span>
            </Button>
          </a>
        </Link>
      </div>

      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${
              imageMedia.length * 320 + (imageMedia.length - 1) * 24
            }px);
          }
        }
        
        .animate-scroll {
          animation: scroll ${imageMedia.length * 4}s linear infinite;
        }
        
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  ) : (
    <></>
  );
};

export default Masonry;
